import React, { memo, useEffect, useState } from "react";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import {
  Button,
  TextField,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  FormControlLabel,
  Checkbox,
  Typography,
  Grid,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { useDispatch, useSelector } from "react-redux";
import { triggerLoader } from "../../redux/reducers/LoaderTrigger";
import configServ from "../../services/config";
import AlertDialog from "../AlertDialog/AlertDialog";
import CheckAndReturn from "../../utility/CheckAndReturn";

function AddSupplierDialog({ open, handleClose, onSupplierAdded }) {
  const dispatch = useDispatch();
  const { user_id, company_id, companyCountry } = useSelector(
    (state) => state.GlobalVariables
  );

  const initialFormData = {
    country: companyCountry || "",
    state: "",
    openingamount: 0,
    status: true,
  };

  const [formData, setFormData] = useState(initialFormData);
  const [validationErrors, setValidationErrors] = useState({});
  const [countryList, setCountryList] = useState([]);
  const [stateList, setStateList] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [title, setTitle] = useState("Alert");
  const [dialogContent, setDialogContent] = useState("");
  const [buttonType, setButtonType] = useState(null);
  const [isSupplierName, setIsSupplierName] = useState(false);

  const handleOpenDialog = (heading, content, type) => {
    setTitle(heading);
    setDialogContent(content);
    setButtonType(type);
    setDialogOpen(true);
  };

  const handleCloseAlertDialog = () => {
    setDialogOpen(false);
    setTitle("Alert");
    setDialogContent("");
    setButtonType(null);
  };

  const clear = () => {
    setFormData({
      ...initialFormData,
      country: companyCountry || "",
    });
    setValidationErrors({});
    setIsSupplierName(false);
  };

  const handleModalClose = () => {
    clear();
    handleClose();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((state) => ({
      ...state,
      [name]: value,
    }));

    if (validationErrors[name]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleChangeCheckbox = (e) => {
    const { name, checked } = e.target;
    setFormData((state) => ({
      ...state,
      [name]: checked,
    }));
  };

  useEffect(() => {
    if (open) {
      clear();
      fetchCountries();
    }
  }, [open]);

  const fetchCountries = async () => {
    try {
      const res = await configServ.getCountry();
      if (res.status === 200) {
        setCountryList(res.data);
      } else {
        setCountryList([]);
      }
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    if (formData.country && countryList.length > 0) {
      fetchStates();
    }
  }, [formData.country, countryList]);

  const fetchStates = async () => {
    try {
      const matchedCountry = countryList.find(
        (x) => x.country === formData.country
      );
      if (!matchedCountry) return;
      const countryId = matchedCountry.PK;
      const res = await configServ.getState({ country: countryId });
      if (res.status === 200) {
        setStateList(res.data);
      } else {
        setStateList([]);
      }
    } catch (e) {
      console.log(e);
    }
  };

  const checkSupplierName = async () => {
    try {
      const dataToSend = {
        company_id: company_id,
        fname: formData.fname,
      };
      const res = await configServ.checkSupplierName(dataToSend);
      if (res.status === 200) {
        setIsSupplierName(true);
      } else {
        setIsSupplierName(false);
      }
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    if (formData.fname) {
      checkSupplierName();
    } else {
      setIsSupplierName(false);
    }
  }, [formData.fname]);

  const Validate = async () => {
    try {
      let errors = {};
      if (!formData.fname) {
        errors.fname = "required";
      } else if (!isSupplierName) {
        errors.fname = "Supplier name already exists or is invalid";
      }
      if (!formData.contact_name) {
        errors.contact_name = "required";
      }
      if (!formData.country) {
        errors.country = "required";
      }
      if (!formData.state) {
        errors.state = "required";
      }
      if (!formData.phone) {
        errors.phone = "required";
      }
      if (!formData.address) {
        errors.address = "required";
      }

      setValidationErrors(errors);
      return Object.values(errors).every((error) => !error);
    } catch (e) {
      console.log(e);
      return false;
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    dispatch(triggerLoader());
    try {
      const isValid = await Validate();
      if (!isValid) {
        return false;
      }
      const dataToSend = {
        ...formData,
        company_id: company_id,
        admin_id: user_id,
      };
      if (formData.is_credit_days === false) {
        delete dataToSend.credit_period;
        delete dataToSend.credit_limit;
      }

      const res = await configServ.addSupplier(dataToSend);
      if (res.status === 200) {
        clear();
        if (onSupplierAdded) {
          onSupplierAdded(res.data);
        }
        handleClose();
      } else if (res.status === 400) {
        handleOpenDialog(
          "Warning",
          res.message || "Failed to create supplier.",
          "warning"
        );
      } else {
        handleOpenDialog(
          "Error",
          res.message || "Failed to create supplier.",
          "error"
        );
      }
    } catch (e) {
      console.log(e);
      handleOpenDialog("Error", "An unexpected error has occurred.", "error");
    } finally {
      dispatch(triggerLoader());
    }
  };

  return (
    <>
      <AlertDialog
        open={dialogOpen}
        setOpen={handleCloseAlertDialog}
        title={title}
        message={dialogContent}
        buttonTitle="Ok"
        buttonType={buttonType}
      />

      <Dialog
        open={open}
        onClose={handleModalClose}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle sx={{ backgroundColor: "#e3f3fc", py: 1.5 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6" fontWeight="bold" color="#1e61b4">
              Add New Supplier
            </Typography>
            <IconButton onClick={handleModalClose} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent dividers sx={{ backgroundColor: "#f9fbfe", py: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography sx={{ fontSize: "16px", fontWeight: "bold", color: "#1e61b4" }}>
                Supplier Detail
              </Typography>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label={
                  <span>
                    Supplier Name<span style={{ color: "red" }}>*</span>
                  </span>
                }
                name="fname"
                onChange={handleChange}
                value={CheckAndReturn.checkTextNullAndUndefined(formData.fname)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {isSupplierName ? (
                        <CheckCircleIcon color="success" />
                      ) : (
                        <CancelIcon color="error" />
                      )}
                    </InputAdornment>
                  ),
                }}
                error={!!validationErrors.fname}
                helperText={validationErrors.fname}
                sx={{ backgroundColor: "white" }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label={
                  <span>
                    Contact Name<span style={{ color: "red" }}>*</span>
                  </span>
                }
                name="contact_name"
                onChange={handleChange}
                value={formData.contact_name || ""}
                error={!!validationErrors.contact_name}
                helperText={validationErrors.contact_name}
                sx={{ backgroundColor: "white" }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small" error={!!validationErrors.country}>
                <InputLabel>
                  Country<span style={{ color: "red" }}>*</span>
                </InputLabel>
                <Select
                  required
                  name="country"
                  label="Country*"
                  value={formData.country || ""}
                  onChange={handleChange}
                  sx={{ backgroundColor: "white" }}
                >
                  {countryList.map((item, index) => (
                    <MenuItem key={index} value={item.country}>
                      {item.country}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small" error={!!validationErrors.state}>
                <InputLabel>
                  State<span style={{ color: "red" }}>*</span>
                </InputLabel>
                <Select
                  required
                  name="state"
                  label="State*"
                  value={formData.state || ""}
                  onChange={handleChange}
                  sx={{ backgroundColor: "white" }}
                >
                  {stateList.map((item, index) => (
                    <MenuItem key={index} value={item.State}>
                      {item.State}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Email"
                name="email"
                onChange={handleChange}
                value={formData.email || ""}
                sx={{ backgroundColor: "white" }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label={
                  <span>
                    Mobile Number<span style={{ color: "red" }}>*</span>
                  </span>
                }
                name="phone"
                type="number"
                onChange={handleChange}
                value={formData.phone || ""}
                error={!!validationErrors.phone}
                helperText={validationErrors.phone}
                sx={{ backgroundColor: "white" }}
              />
            </Grid>

            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                size="small"
                label={
                  <span>
                    Address<span style={{ color: "red" }}>*</span>
                  </span>
                }
                name="address"
                onChange={handleChange}
                value={formData.address || ""}
                error={!!validationErrors.address}
                helperText={validationErrors.address}
                sx={{ backgroundColor: "white" }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                size="small"
                label="Opening Amount"
                name="openingamount"
                onChange={handleChange}
                value={formData.openingamount || ""}
                sx={{ backgroundColor: "white" }}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography sx={{ fontSize: "16px", fontWeight: "bold", color: "#1e61b4", mt: 1 }}>
                Tax Detail
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="PAN/NO"
                name="pan_no"
                onChange={handleChange}
                value={formData.pan_no || ""}
                sx={{ backgroundColor: "white" }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="GSTIN/UIN"
                name="gst_uin"
                onChange={handleChange}
                value={formData.gst_uin || ""}
                sx={{ backgroundColor: "white" }}
              />
            </Grid>

            <Grid item xs={12} md={8}>
              <Grid
                container
                border="solid lightgrey 1px"
                borderRadius="4px"
                spacing={1}
                p={1}
                bgcolor="#f4e7b7"
              >
                <Grid item xs={12}>
                  <FormControlLabel
                    control={<Checkbox />}
                    name="is_credit_days"
                    checked={Boolean(formData.is_credit_days)}
                    onChange={handleChangeCheckbox}
                    label="Check credit days"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Credit Period"
                    size="small"
                    name="credit_period"
                    InputProps={{
                      endAdornment: "d",
                    }}
                    disabled={!formData.is_credit_days}
                    onChange={handleChange}
                    value={formData.credit_period || ""}
                    sx={{ backgroundColor: "white" }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Credit Limit"
                    size="small"
                    name="credit_limit"
                    disabled={!formData.is_credit_days}
                    onChange={handleChange}
                    value={formData.credit_limit || ""}
                    sx={{ backgroundColor: "white" }}
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} md={4} display="flex" alignItems="center" justifyContent="flex-end">
              <FormControlLabel
                control={<Checkbox />}
                name="status"
                checked={Boolean(formData.status)}
                onChange={handleChangeCheckbox}
                label="Status"
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ backgroundColor: "#e3f3fc", px: 3, py: 1.5 }}>
          <Button
            onClick={clear}
            variant="contained"
            color="secondary"
            startIcon={<RotateLeftIcon />}
            sx={{ mx: 1 }}
          >
            Reset
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            startIcon={<SaveIcon />}
            color="primary"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default memo(AddSupplierDialog);
